/**
 * Boss-Specific Chests
 *
 * Each boss drops their own unique chest with themed loot.
 * Boss chests contain:
 * - Boss-specific unique equipment (only drops from this boss)
 * - Themed materials and consumables
 * - Higher rarity chances than regular chests
 *
 * Structure:
 * - bossId: Links to the boss in mobs.js
 * - uniqueEquipment: Array of equipment IDs exclusive to this boss
 * - dropTable: Category weights and rarity distribution
 */

import { ITEM_CATEGORIES } from './consumables';

// ============================================================
// TIER 1 BOSS CHESTS (Ancient Ruins, Goblin Cave, Bandit Hideout, Haunted Mine)
// ============================================================

export const BOSS_CHESTS = {
  // -----------------------------------------------------------
  // OGRE CHIEFTAIN - Ancient Ruins
  // -----------------------------------------------------------
  chest_boss_ogre: {
    id: 'chest_boss_ogre',
    bossId: 'boss_ogre',
    name: "Ogre Chieftain's Hoard",
    icon: '👿',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'rare',
    tier: 1,
    description: 'A crude chest filled with the Ogre Chieftain\'s treasures. Contains the legendary Ogre\'s Maul.',
    stackSize: 99,
    usable: true,
    sellValue: 100,
    dropTable: {
      equipment: 50,
      material: 30,
      consumable: 20,
      equipmentRarity: {
        common: 20,
        uncommon: 40,
        rare: 30,
        epic: 10,
      },
      // Unique equipment that can ONLY drop from this chest
      uniqueEquipment: ['ogresMaul', 'chieftainsBelt', 'thickHideVest'],
      // Regular equipment pool (in addition to uniques)
      equipmentPool: ['woodenClub', 'leatherVest', 'ironHelmet'],
      materials: ['bone', 'leather', 'thickHide', 'ogreHeart'],
      consumables: ['minorHealthPotion', 'healthPotion', 'strengthPotion'],
    },
    guaranteedDrops: 2,
    bonusDropChance: 0.4,
    maxBonusDrops: 2,
    dungeonLevel: 10,
    // Chance for unique equipment to drop (per drop slot)
    uniqueDropChance: 0.15,
  },

  // -----------------------------------------------------------
  // GOBLIN KING - Goblin Cave
  // -----------------------------------------------------------
  chest_boss_goblinKing: {
    id: 'chest_boss_goblinKing',
    bossId: 'boss_goblinKing',
    name: "Goblin King's Treasury",
    icon: '👑',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'rare',
    tier: 1,
    description: 'A glittering chest overflowing with the Goblin King\'s stolen riches. Contains the Crown of Greed.',
    stackSize: 99,
    usable: true,
    sellValue: 120,
    dropTable: {
      equipment: 45,
      material: 35,
      consumable: 20,
      equipmentRarity: {
        common: 25,
        uncommon: 40,
        rare: 28,
        epic: 7,
      },
      uniqueEquipment: ['crownOfGreed', 'goblinKingsScepter', 'thievesCloak'],
      equipmentPool: ['rustyDagger', 'leatherVest', 'leatherBoots'],
      materials: ['gold', 'leather', 'wolfFang', 'fur', 'goblinCrown'],
      consumables: ['minorHealthPotion', 'healthPotion'],
    },
    guaranteedDrops: 2,
    bonusDropChance: 0.45,
    maxBonusDrops: 2,
    dungeonLevel: 10,
    uniqueDropChance: 0.15,
  },

  // -----------------------------------------------------------
  // BANDIT LORD - Bandit Hideout
  // -----------------------------------------------------------
  chest_boss_banditLord: {
    id: 'chest_boss_banditLord',
    bossId: 'boss_banditLord',
    name: "Bandit Lord's Stash",
    icon: '🏴‍☠️',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'rare',
    tier: 1,
    description: 'A reinforced chest containing the Bandit Lord\'s prized possessions. Contains the Blade of Betrayal.',
    stackSize: 99,
    usable: true,
    sellValue: 150,
    dropTable: {
      equipment: 55,
      material: 25,
      consumable: 20,
      equipmentRarity: {
        common: 15,
        uncommon: 45,
        rare: 30,
        epic: 10,
      },
      uniqueEquipment: ['bladeOfBetrayal', 'banditLordsMask', 'shadowstepBoots'],
      equipmentPool: ['steelSword', 'woodenShield', 'chainmail', 'leatherBoots'],
      materials: ['gold', 'ironOre', 'leather', 'banditMedallion'],
      consumables: ['minorHealthPotion', 'healthPotion', 'strengthPotion'],
    },
    guaranteedDrops: 2,
    bonusDropChance: 0.4,
    maxBonusDrops: 2,
    dungeonLevel: 10,
    uniqueDropChance: 0.18,
  },

  // -----------------------------------------------------------
  // SPECTRAL OVERSEER - Haunted Mine
  // -----------------------------------------------------------
  chest_boss_mineOverseer: {
    id: 'chest_boss_mineOverseer',
    bossId: 'boss_mineOverseer',
    name: "Overseer's Ghostly Cache",
    icon: '👻',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'rare',
    tier: 1,
    description: 'A spectral chest phasing in and out of reality. Contains the Phantom Pickaxe.',
    stackSize: 99,
    usable: true,
    sellValue: 100,
    dropTable: {
      equipment: 45,
      material: 40,
      consumable: 15,
      equipmentRarity: {
        common: 20,
        uncommon: 40,
        rare: 30,
        epic: 10,
      },
      uniqueEquipment: ['phantomPickaxe', 'spectersLantern', 'etherealChains'],
      equipmentPool: ['ironHelmet', 'chainmail', 'leatherGloves'],
      materials: ['ironOre', 'coal', 'ectoplasm', 'cursedGem', 'ghostlyEssence'],
      consumables: ['minorHealthPotion', 'healthPotion'],
    },
    guaranteedDrops: 2,
    bonusDropChance: 0.35,
    maxBonusDrops: 2,
    dungeonLevel: 10,
    uniqueDropChance: 0.15,
  },

  // ============================================================
  // TIER 2 BOSS CHESTS (Dark Catacombs, Frozen Crypt, Plague Tunnels, Orc Fortress)
  // ============================================================

  // -----------------------------------------------------------
  // LICH - Dark Catacombs
  // -----------------------------------------------------------
  chest_boss_lich: {
    id: 'chest_boss_lich',
    bossId: 'boss_lich',
    name: "Lich's Phylactery Vault",
    icon: '💀',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'epic',
    tier: 2,
    description: 'An ancient vault protected by dark magic. Contains the Staff of Undeath.',
    stackSize: 99,
    usable: true,
    sellValue: 300,
    dropTable: {
      equipment: 55,
      material: 25,
      consumable: 20,
      equipmentRarity: {
        uncommon: 25,
        rare: 45,
        epic: 25,
        legendary: 5,
      },
      uniqueEquipment: ['staffOfUndeath', 'lichsPhylactery', 'robesOfTheDamned'],
      equipmentPool: ['magicStaff', 'darkRobes', 'skullHelmet'],
      materials: ['bone', 'ectoplasm', 'darkSteel', 'soulShard', 'necroticEssence'],
      consumables: ['healthPotion', 'greaterHealthPotion', 'defensePotion'],
    },
    guaranteedDrops: 3,
    bonusDropChance: 0.4,
    maxBonusDrops: 2,
    dungeonLevel: 25,
    uniqueDropChance: 0.12,
  },

  // -----------------------------------------------------------
  // FROST LICH - Frozen Crypt
  // -----------------------------------------------------------
  chest_boss_frostLich: {
    id: 'chest_boss_frostLich',
    bossId: 'boss_frostLich',
    name: "Frost Lich's Frozen Tomb",
    icon: '🥶',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'epic',
    tier: 2,
    description: 'A chest encased in eternal ice. Contains the Frostbite Scepter.',
    stackSize: 99,
    usable: true,
    sellValue: 320,
    dropTable: {
      equipment: 55,
      material: 25,
      consumable: 20,
      equipmentRarity: {
        uncommon: 20,
        rare: 45,
        epic: 28,
        legendary: 7,
      },
      uniqueEquipment: ['frostbiteScepter', 'cloakOfEternalWinter', 'icecrownCirclet'],
      equipmentPool: ['magicStaff', 'plateBoots', 'steelHelmet'],
      materials: ['crystalShard', 'frozenCore', 'ectoplasm', 'permafrost'],
      consumables: ['healthPotion', 'greaterHealthPotion', 'swiftnessPotion'],
    },
    guaranteedDrops: 3,
    bonusDropChance: 0.42,
    maxBonusDrops: 2,
    dungeonLevel: 25,
    uniqueDropChance: 0.12,
  },

  // -----------------------------------------------------------
  // PLAGUE RAT - Plague Tunnels
  // -----------------------------------------------------------
  chest_boss_plagueRat: {
    id: 'chest_boss_plagueRat',
    bossId: 'boss_plagueRat',
    name: "Plague Rat's Diseased Nest",
    icon: '🐀',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'epic',
    tier: 2,
    description: 'A festering pile of infected treasures. Contains the Virulent Fang.',
    stackSize: 99,
    usable: true,
    sellValue: 280,
    dropTable: {
      equipment: 50,
      material: 30,
      consumable: 20,
      equipmentRarity: {
        uncommon: 25,
        rare: 45,
        epic: 24,
        legendary: 6,
      },
      uniqueEquipment: ['virulentFang', 'plaguebearersMask', 'infectedClaws'],
      equipmentPool: ['poisonDagger', 'leatherVest', 'thinCloak'],
      materials: ['toxin', 'diseasedFlesh', 'ratTail', 'plagueEssence'],
      consumables: ['healthPotion', 'greaterHealthPotion', 'antidote'],
    },
    guaranteedDrops: 3,
    bonusDropChance: 0.38,
    maxBonusDrops: 2,
    dungeonLevel: 25,
    uniqueDropChance: 0.12,
  },

  // -----------------------------------------------------------
  // ORC WARCHIEF - Orc Fortress
  // -----------------------------------------------------------
  chest_boss_orcWarchief: {
    id: 'chest_boss_orcWarchief',
    bossId: 'boss_orcWarchief',
    name: "Warchief's War Chest",
    icon: '⚔️',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'epic',
    tier: 2,
    description: 'A massive iron chest filled with spoils of war. Contains the Orcish Warblade.',
    stackSize: 99,
    usable: true,
    sellValue: 350,
    dropTable: {
      equipment: 60,
      material: 25,
      consumable: 15,
      equipmentRarity: {
        uncommon: 20,
        rare: 40,
        epic: 30,
        legendary: 10,
      },
      uniqueEquipment: ['orcishWarblade', 'warchiefsBanner', 'skullCrusherHelm'],
      equipmentPool: ['greataxe', 'platemail', 'steelBoots', 'ironShield'],
      materials: ['ironOre', 'darkSteel', 'orcishSteel', 'warTrophy'],
      consumables: ['healthPotion', 'greaterHealthPotion', 'strengthPotion'],
    },
    guaranteedDrops: 3,
    bonusDropChance: 0.45,
    maxBonusDrops: 2,
    dungeonLevel: 25,
    uniqueDropChance: 0.14,
  },

  // ============================================================
  // TIER 3 BOSS CHESTS (Corrupted Temple, Dragon's Lair, Demon Citadel, Witch's Coven)
  // ============================================================

  // -----------------------------------------------------------
  // DRAGON - Dragon's Lair
  // -----------------------------------------------------------
  chest_boss_dragon: {
    id: 'chest_boss_dragon',
    bossId: 'boss_dragon',
    name: "Dragon's Ancient Hoard",
    icon: '🐉',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'epic',
    tier: 3,
    description: 'A legendary hoard accumulated over centuries. Contains the Dragonslayer\'s Edge.',
    stackSize: 99,
    usable: true,
    sellValue: 800,
    dropTable: {
      equipment: 60,
      material: 25,
      consumable: 15,
      equipmentRarity: {
        rare: 30,
        epic: 45,
        legendary: 20,
        mythic: 5,
      },
      uniqueEquipment: ['dragonslayersEdge', 'dragonscaleArmor', 'wyrmfireAmulet'],
      equipmentPool: ['titanSword', 'platemail', 'dragonHelmet'],
      materials: ['dragonScale', 'dragonHeart', 'dragonbone', 'fireEssence'],
      consumables: ['greaterHealthPotion', 'superiorHealthPotion', 'elixirOfPower'],
    },
    guaranteedDrops: 3,
    bonusDropChance: 0.5,
    maxBonusDrops: 3,
    dungeonLevel: 45,
    uniqueDropChance: 0.10,
  },

  // -----------------------------------------------------------
  // ELDER WYRM - Elder Dragon's Roost
  // -----------------------------------------------------------
  chest_boss_elderWyrm: {
    id: 'chest_boss_elderWyrm',
    bossId: 'boss_elderWyrm',
    name: "Elder Wyrm's Millennia Trove",
    icon: '🦎',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'epic',
    tier: 3,
    description: 'An impossibly ancient collection of treasures. Contains the Primordial Fang.',
    stackSize: 99,
    usable: true,
    sellValue: 900,
    dropTable: {
      equipment: 60,
      material: 25,
      consumable: 15,
      equipmentRarity: {
        rare: 25,
        epic: 45,
        legendary: 23,
        mythic: 7,
      },
      uniqueEquipment: ['primordialFang', 'ancientWyrmscale', 'eldersCrown'],
      equipmentPool: ['titanSword', 'dragonmail', 'ancientHelmet'],
      materials: ['dragonScale', 'elderEssence', 'primordialShard', 'ancientBone'],
      consumables: ['greaterHealthPotion', 'superiorHealthPotion', 'elixirOfPower'],
    },
    guaranteedDrops: 3,
    bonusDropChance: 0.52,
    maxBonusDrops: 3,
    dungeonLevel: 45,
    uniqueDropChance: 0.10,
  },

  // -----------------------------------------------------------
  // DEMON PRINCE - Demon Citadel
  // -----------------------------------------------------------
  chest_boss_demonPrince: {
    id: 'chest_boss_demonPrince',
    bossId: 'boss_demonPrince',
    name: "Demon Prince's Infernal Coffer",
    icon: '😈',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'epic',
    tier: 3,
    description: 'A chest forged in hellfire itself. Contains the Blade of the Damned.',
    stackSize: 99,
    usable: true,
    sellValue: 850,
    dropTable: {
      equipment: 55,
      material: 28,
      consumable: 17,
      equipmentRarity: {
        rare: 28,
        epic: 45,
        legendary: 22,
        mythic: 5,
      },
      uniqueEquipment: ['bladeOfTheDamned', 'demonPrincesHorns', 'infernalWings'],
      equipmentPool: ['demonBlade', 'hellforgedArmor', 'obsidianShield'],
      materials: ['demonHorn', 'sulfur', 'infernalCore', 'brimstone'],
      consumables: ['greaterHealthPotion', 'superiorHealthPotion', 'elixirOfPower'],
    },
    guaranteedDrops: 3,
    bonusDropChance: 0.48,
    maxBonusDrops: 3,
    dungeonLevel: 45,
    uniqueDropChance: 0.10,
  },

  // -----------------------------------------------------------
  // GRAND WITCH - Witch's Coven
  // -----------------------------------------------------------
  chest_boss_grandWitch: {
    id: 'chest_boss_grandWitch',
    bossId: 'boss_grandWitch',
    name: "Grand Witch's Grimoire Chest",
    icon: '🧙‍♀️',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'epic',
    tier: 3,
    description: 'A chest overflowing with forbidden artifacts. Contains the Hexmaster\'s Staff.',
    stackSize: 99,
    usable: true,
    sellValue: 780,
    dropTable: {
      equipment: 50,
      material: 32,
      consumable: 18,
      equipmentRarity: {
        rare: 30,
        epic: 45,
        legendary: 20,
        mythic: 5,
      },
      uniqueEquipment: ['hexmastersStaff', 'covensCrown', 'robesOfTheGrandWitch'],
      equipmentPool: ['witchStaff', 'enchantedRobes', 'magicOrb'],
      materials: ['witchHair', 'cursedTome', 'hexEssence', 'moonPetal'],
      consumables: ['greaterHealthPotion', 'superiorHealthPotion', 'scrollOfPrecision'],
    },
    guaranteedDrops: 3,
    bonusDropChance: 0.45,
    maxBonusDrops: 3,
    dungeonLevel: 45,
    uniqueDropChance: 0.10,
  },

  // ============================================================
  // TIER 4 BOSS CHESTS (Abyssal Depths, Inferno Core, Nightmare Realm, Mad Archives)
  // ============================================================

  // -----------------------------------------------------------
  // ABYSSAL LORD - Abyssal Depths
  // -----------------------------------------------------------
  chest_boss_abyssalLord: {
    id: 'chest_boss_abyssalLord',
    bossId: 'boss_abyssalLord',
    name: "Abyssal Lord's Sunken Vault",
    icon: '🌊',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'legendary',
    tier: 4,
    description: 'A vault from the deepest ocean trenches. Contains the Trident of the Abyss.',
    stackSize: 99,
    usable: true,
    sellValue: 1500,
    dropTable: {
      equipment: 60,
      material: 25,
      consumable: 15,
      equipmentRarity: {
        rare: 15,
        epic: 40,
        legendary: 35,
        mythic: 10,
      },
      uniqueEquipment: ['tridentOfTheAbyss', 'abyssalLordsCrown', 'depthsEmbrace'],
      equipmentPool: ['voidBlade', 'abyssalArmor', 'darkwaterShield'],
      materials: ['voidFragment', 'abyssalPearl', 'depthsCore', 'drownedSoul'],
      consumables: ['superiorHealthPotion', 'elixirOfPower', 'scrollOfFortitude'],
    },
    guaranteedDrops: 4,
    bonusDropChance: 0.5,
    maxBonusDrops: 3,
    dungeonLevel: 65,
    uniqueDropChance: 0.08,
  },

  // -----------------------------------------------------------
  // INFERNO LORD - Inferno Core
  // -----------------------------------------------------------
  chest_boss_infernoLord: {
    id: 'chest_boss_infernoLord',
    bossId: 'boss_infernoLord',
    name: "Inferno Lord's Molten Treasury",
    icon: '🔥',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'legendary',
    tier: 4,
    description: 'A chest of living magma. Contains the Inferno Lord\'s Heart.',
    stackSize: 99,
    usable: true,
    sellValue: 1600,
    dropTable: {
      equipment: 60,
      material: 25,
      consumable: 15,
      equipmentRarity: {
        rare: 12,
        epic: 38,
        legendary: 38,
        mythic: 12,
      },
      uniqueEquipment: ['infernoLordsHeart', 'flameImperatorsCrown', 'magmaforgedGreaves'],
      equipmentPool: ['blazingSword', 'volcanicArmor', 'emberShield'],
      materials: ['magmaCore', 'livingFlame', 'volcanicGlass', 'infernoEssence'],
      consumables: ['superiorHealthPotion', 'elixirOfPower', 'blessingToken'],
    },
    guaranteedDrops: 4,
    bonusDropChance: 0.52,
    maxBonusDrops: 3,
    dungeonLevel: 65,
    uniqueDropChance: 0.08,
  },

  // -----------------------------------------------------------
  // NIGHTMARE KING - Nightmare Realm
  // -----------------------------------------------------------
  chest_boss_nightmareKing: {
    id: 'chest_boss_nightmareKing',
    bossId: 'boss_nightmareKing',
    name: "Nightmare King's Dream Vault",
    icon: '😱',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'legendary',
    tier: 4,
    description: 'A chest that exists between dreams and reality. Contains the Nightmare\'s Edge.',
    stackSize: 99,
    usable: true,
    sellValue: 1550,
    dropTable: {
      equipment: 58,
      material: 27,
      consumable: 15,
      equipmentRarity: {
        rare: 14,
        epic: 40,
        legendary: 36,
        mythic: 10,
      },
      uniqueEquipment: ['nightmaresEdge', 'crownOfTerrors', 'dreamwalkersCape'],
      equipmentPool: ['shadowBlade', 'nightmareArmor', 'terrorShield'],
      materials: ['nightmareEssence', 'dreamShard', 'fearFragment', 'sleeplessSoul'],
      consumables: ['superiorHealthPotion', 'elixirOfPower', 'scrollOfPrecision'],
    },
    guaranteedDrops: 4,
    bonusDropChance: 0.48,
    maxBonusDrops: 3,
    dungeonLevel: 65,
    uniqueDropChance: 0.08,
  },

  // -----------------------------------------------------------
  // MAD ARCHIVIST - Mad Archives
  // -----------------------------------------------------------
  chest_boss_madArchivist: {
    id: 'chest_boss_madArchivist',
    bossId: 'boss_madArchivist',
    name: "Mad Archivist's Forbidden Collection",
    icon: '📚',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'legendary',
    tier: 4,
    description: 'A chest of reality-warping knowledge. Contains the Tome of Madness.',
    stackSize: 99,
    usable: true,
    sellValue: 1450,
    dropTable: {
      equipment: 55,
      material: 30,
      consumable: 15,
      equipmentRarity: {
        rare: 15,
        epic: 42,
        legendary: 35,
        mythic: 8,
      },
      uniqueEquipment: ['tomeOfMadness', 'archivistsGlasses', 'robesOfForbiddenKnowledge'],
      equipmentPool: ['knowledgeStaff', 'scholarRobes', 'wisdomOrb'],
      materials: ['forbiddenPage', 'madnessInk', 'realityFragment', 'knowledgeEssence'],
      consumables: ['superiorHealthPotion', 'elixirOfPower', 'blessingToken'],
    },
    guaranteedDrops: 4,
    bonusDropChance: 0.45,
    maxBonusDrops: 3,
    dungeonLevel: 65,
    uniqueDropChance: 0.08,
  },

  // ============================================================
  // TIER 5 BOSS CHESTS (Void Citadel, Celestial Breach, Reality Rift, Entropy Core)
  // ============================================================

  // -----------------------------------------------------------
  // VOID GOD - Void Citadel
  // -----------------------------------------------------------
  chest_boss_voidGod: {
    id: 'chest_boss_voidGod',
    bossId: 'boss_voidGod',
    name: "Void God's Cosmic Reliquary",
    icon: '🌌',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'legendary',
    tier: 5,
    description: 'A chest containing fragments of existence itself. Contains the Void God\'s Essence.',
    stackSize: 99,
    usable: true,
    sellValue: 5000,
    dropTable: {
      equipment: 65,
      material: 22,
      consumable: 13,
      equipmentRarity: {
        epic: 25,
        legendary: 50,
        mythic: 25,
      },
      uniqueEquipment: ['voidGodsEssence', 'crownOfTheVoid', 'cosmicDestroyer'],
      equipmentPool: ['voidBlade', 'cosmicArmor', 'dimensionalShield'],
      materials: ['voidEssence', 'cosmicFragment', 'divineEssence', 'realityCore'],
      consumables: ['superiorHealthPotion', 'elixirOfPower', 'blessingToken', 'luckyCharm'],
    },
    guaranteedDrops: 4,
    bonusDropChance: 0.6,
    maxBonusDrops: 3,
    dungeonLevel: 85,
    uniqueDropChance: 0.06,
  },

  // -----------------------------------------------------------
  // FALLEN SERAPH - Celestial Breach
  // -----------------------------------------------------------
  chest_boss_fallenSeraph: {
    id: 'chest_boss_fallenSeraph',
    bossId: 'boss_fallenSeraph',
    name: "Fallen Seraph's Divine Cache",
    icon: '👼',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'legendary',
    tier: 5,
    description: 'A chest of corrupted divine artifacts. Contains the Seraph\'s Corrupted Halo.',
    stackSize: 99,
    usable: true,
    sellValue: 5200,
    dropTable: {
      equipment: 65,
      material: 22,
      consumable: 13,
      equipmentRarity: {
        epic: 22,
        legendary: 52,
        mythic: 26,
      },
      uniqueEquipment: ['corruptedHalo', 'fallenWings', 'divineJudgement'],
      equipmentPool: ['holyBlade', 'seraphicArmor', 'celestialShield'],
      materials: ['divineEssence', 'fallenFeather', 'corruptedLight', 'celestialTear'],
      consumables: ['superiorHealthPotion', 'elixirOfPower', 'blessingToken', 'luckyCharm'],
    },
    guaranteedDrops: 4,
    bonusDropChance: 0.58,
    maxBonusDrops: 3,
    dungeonLevel: 85,
    uniqueDropChance: 0.06,
  },

  // -----------------------------------------------------------
  // REALITY BREAKER - Reality Rift
  // -----------------------------------------------------------
  chest_boss_realityBreaker: {
    id: 'chest_boss_realityBreaker',
    bossId: 'boss_realityBreaker',
    name: "Reality Breaker's Paradox Box",
    icon: '🔮',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'legendary',
    tier: 5,
    description: 'A chest that exists in all timelines simultaneously. Contains the Shard of Broken Reality.',
    stackSize: 99,
    usable: true,
    sellValue: 5500,
    dropTable: {
      equipment: 65,
      material: 22,
      consumable: 13,
      equipmentRarity: {
        epic: 20,
        legendary: 50,
        mythic: 30,
      },
      uniqueEquipment: ['shardOfBrokenReality', 'paradoxHelm', 'dimensionRipper'],
      equipmentPool: ['realityBlade', 'paradoxArmor', 'temporalShield'],
      materials: ['realityFragment', 'paradoxCore', 'temporalEssence', 'dimensionalTear'],
      consumables: ['superiorHealthPotion', 'elixirOfPower', 'blessingToken', 'luckyCharm'],
    },
    guaranteedDrops: 4,
    bonusDropChance: 0.62,
    maxBonusDrops: 3,
    dungeonLevel: 85,
    uniqueDropChance: 0.06,
  },

  // -----------------------------------------------------------
  // ENTROPY SOVEREIGN - Entropy Core
  // -----------------------------------------------------------
  chest_boss_entropySovereign: {
    id: 'chest_boss_entropySovereign',
    bossId: 'boss_entropySovereign',
    name: "Entropy Sovereign's Final Decay",
    icon: '💀',
    category: ITEM_CATEGORIES.LOOTBOX,
    rarity: 'legendary',
    tier: 5,
    description: 'A chest containing the end of all things. Contains the Entropy Sovereign\'s Crown.',
    stackSize: 99,
    usable: true,
    sellValue: 6000,
    dropTable: {
      equipment: 68,
      material: 20,
      consumable: 12,
      equipmentRarity: {
        epic: 18,
        legendary: 47,
        mythic: 35,
      },
      uniqueEquipment: ['entropySovereignsCrown', 'decayBringer', 'robesOfEndingTime'],
      equipmentPool: ['entropicBlade', 'decayArmor', 'nullificationShield'],
      materials: ['entropyEssence', 'decayCore', 'endFragment', 'nullShard'],
      consumables: ['superiorHealthPotion', 'elixirOfPower', 'blessingToken', 'luckyCharm'],
    },
    guaranteedDrops: 5,
    bonusDropChance: 0.65,
    maxBonusDrops: 3,
    dungeonLevel: 85,
    uniqueDropChance: 0.05,
  },
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Get boss chest by boss ID
 * @param {string} bossId - The boss ID (e.g., 'boss_ogre')
 * @returns {Object|null} Boss chest definition or null if not found
 */
export function getBossChest(bossId) {
  const chestId = `chest_${bossId}`;
  return BOSS_CHESTS[chestId] || null;
}

/**
 * Get all boss chest IDs
 * @returns {string[]} Array of boss chest IDs
 */
export function getAllBossChestIds() {
  return Object.keys(BOSS_CHESTS);
}

/**
 * Check if a chest is a boss chest
 * @param {string} chestId - The chest ID
 * @returns {boolean}
 */
export function isBossChest(chestId) {
  return chestId in BOSS_CHESTS;
}

/**
 * Get boss chest for a specific boss and dungeon level
 * Creates an instance with the correct dungeon level
 * @param {string} bossId - The boss ID
 * @param {number} dungeonLevel - The dungeon level for equipment scaling
 * @returns {Object|null} Boss chest instance or null
 */
export function createBossChestInstance(bossId, dungeonLevel) {
  const chest = getBossChest(bossId);
  if (!chest) return null;

  return {
    ...chest,
    instanceId: `${chest.id}_${Date.now()}_${Math.floor(Math.random() * 10000)}`,
    quantity: 1,
    dungeonLevel: dungeonLevel || chest.dungeonLevel,
  };
}

export default BOSS_CHESTS;
